/**
 * useAlignMeasurementsSync
 *
 * Listens for measurement events from the remote renderer and persists them
 * to the database. Consumers can use the onMeasurementsSynced callback to
 * refresh queries once new measurements have been saved.
 *
 * Flow:
 *   Remote Renderer → Event → useAlignMeasurementsSync → Database (instances + plans) → onMeasurementsSynced
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { EventsEnum } from "../../../classes/enums/EventsEnum";

const useAlignMeasurementsSync = ({
  instances,
  currentPlan,
  currentPlanId,
  isComparisonMode = false,
  syncMeasurementsWithInstances,
  syncMeasurementsWithPlan,
  onMeasurementsSynced,
}) => {
  const [latestMeasurementEvent, setLatestMeasurementEvent] = useState(null);
  const pendingMeasurementsRef = useRef(new Map());
  const lastSequenceRef = useRef(0);

  const syncMeasurementsToDatabase = useCallback(
    async (measurementPayload, planOverride = null) => {
      const syncResult = {
        original: null,
        current: null,
        target: null,
        planId: null,
      };

      const planContext = planOverride || {
        planId: currentPlanId,
        planRef: currentPlan,
      };
      syncResult.planId = planContext.planId || null;

      try {
        if (
          measurementPayload.original?.data &&
          instances &&
          Object.keys(instances).length > 0
        ) {
          syncResult.original = await syncMeasurementsWithInstances(
            instances,
            measurementPayload.original.data
          );
        }

        if (
          measurementPayload.current?.data &&
          planContext.planId &&
          planContext.planRef
        ) {
          syncResult.current = await syncMeasurementsWithPlan(
            planContext.planId,
            planContext.planRef,
            measurementPayload.current.data,
            "current"
          );
        }

        if (
          measurementPayload.target?.data &&
          planContext.planId &&
          planContext.planRef
        ) {
          syncResult.target = await syncMeasurementsWithPlan(
            planContext.planId,
            planContext.planRef,
            measurementPayload.target.data,
            "target"
          );
        }

        if (
          onMeasurementsSynced &&
          [syncResult.original, syncResult.current, syncResult.target].some(
            result => result?.createdIds?.length || result?.updated
          )
        ) {
          onMeasurementsSynced(syncResult);
        }
      } catch (error) {}

      return syncResult;
    },
    [
      instances,
      currentPlanId,
      currentPlan,
      syncMeasurementsWithInstances,
      syncMeasurementsWithPlan,
      onMeasurementsSynced,
    ]
  );

  const handleMeasurementsEvent = useCallback(
    async event => {
      if (isComparisonMode) {
        return;
      }

      try {
        const newMeasurements = event.detail;
        if (newMeasurements?.[0]) {
          const sampleTarget =
            newMeasurements[0].target?.data?.lateral?.annotations;
          const sampleCurrent =
            newMeasurements[0].current?.data?.lateral?.annotations;
          const targetEntry = sampleTarget
            ? Object.values(sampleTarget)[0]
            : null;
          const currentEntry = sampleCurrent
            ? Object.values(sampleCurrent)[0]
            : null;
        }
        if (!newMeasurements?.[0]) {
          return;
        }

        const measurement = newMeasurements[0];
        const sequence = Date.now();
        if (sequence <= lastSequenceRef.current) {
          return;
        }
        lastSequenceRef.current = sequence;

        const findMarker = data => {
          if (!data?.lateral?.annotations && !data?.frontal?.annotations) {
            return null;
          }
          const fromLateral = data.lateral?.annotations
            ? Object.values(data.lateral.annotations).find(
                entry => entry?.unique_identifier === 14014
              )
            : null;
          const fromFrontal =
            !fromLateral && data.frontal?.annotations
              ? Object.values(data.frontal.annotations).find(
                  entry => entry?.unique_identifier === 14014
                )
              : null;
          const picked = fromLateral || fromFrontal;
          return picked
            ? {
                unique_identifier: picked.unique_identifier,
                value: picked.representation?.value,
                source: fromLateral ? "lateral" : "frontal",
              }
            : null;
        };

        const measurementSignature = JSON.stringify({
          original: measurement.original?.data?.lateral?.annotations,
          current: measurement.current?.data?.lateral?.annotations,
          target: measurement.target?.data?.lateral?.annotations,
        });

        const hasPayload =
          measurement.original?.data ||
          measurement.current?.data ||
          measurement.target?.data;

        const syncResult = hasPayload
          ? await syncMeasurementsToDatabase(measurement)
          : null;

        setLatestMeasurementEvent({
          data: measurement,
          signature: measurementSignature,
          receivedAt: Date.now(),
          syncResult,
        });
      } catch (error) {}
    },
    [syncMeasurementsToDatabase]
  );

  useEffect(() => {
    if (isComparisonMode) {
      return;
    }

    window.addEventListener(EventsEnum.GET_MEASUREMENTS, handleMeasurementsEvent);

    return () => {
      window.removeEventListener(
        EventsEnum.GET_MEASUREMENTS,
        handleMeasurementsEvent
      );
    };
  }, [handleMeasurementsEvent, isComparisonMode]);

  useEffect(() => {
    pendingMeasurementsRef.current = new Map();
    setLatestMeasurementEvent(null);
    lastSequenceRef.current = 0;
  }, [currentPlanId, isComparisonMode]);

  useEffect(() => {
    const pendingForCurrent =
      currentPlan && currentPlanId
        ? pendingMeasurementsRef.current.get(currentPlanId)
        : null;
    if (pendingForCurrent) {
      syncMeasurementsToDatabase(pendingForCurrent, {
        planId: currentPlanId,
        planRef: currentPlan,
      });
      pendingMeasurementsRef.current.delete(currentPlanId);
    }
  }, [
    currentPlan,
    currentPlanId,
    syncMeasurementsToDatabase,
  ]);

  return { latestMeasurementEvent };
};

export default useAlignMeasurementsSync;
